import React, {useState, useEffect} from "react";
import ReactDOM from "react-dom";
import {Plus, X, ChevronDown, ChevronRight} from "lucide-react";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import {fetchAllRoles, updateUserRole} from "../api";
import {useRowActionMenu} from "../../../../hooks/useRowActionMenu";
import Cookies from "js-cookie";

// Portals the panel into document.body (same pattern as the row "..." action menu in
// TeamTable.js, via the same shared hook) instead of a plain `position: absolute` child --
// this row sits inside a `overflow-x-auto` table wrapper, and setting overflow-x to anything
// but visible forces the browser to compute overflow-y as auto too, so a plain absolute
// dropdown gets clipped at the table wrapper's edge instead of floating freely.
const RoleDropdown = ({userEmail, userRoles = [], onRoleChange}) => {
    const [allRoles, setAllRoles] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [loading, setLoading] = useState(false);
    const {openRowId, position, dropdownRef, handleActionClick} =
        useRowActionMenu("data-role-trigger");
    const open = openRowId === userEmail;
    const token = Cookies.get("authToken");

    const userRoleIds = userRoles.map((r) => r.id);

    useEffect(() => {
        if (open) {
            setLoading(true);
            fetchAllRoles(token)
                .then((res) => setAllRoles(res.data || []))
                .finally(() => setLoading(false));
        }
    }, [open, token]);

    const handleCheckbox = async (roleId, checked) => {
        try {
            setLoading(true);
            await updateUserRole(userEmail, roleId, checked, token);
            onRoleChange && onRoleChange();
        } finally {
            setLoading(false);
        }
    };

    const toggleExpand = (roleId) => {
        setExpanded((prev) => ({...prev, [roleId]: !prev[roleId]}));
    };

    return (
        <div className="relative inline-block">
            {/* === Trigger Button === */}
            <button
                data-role-trigger
                onClick={(e) => handleActionClick(userEmail, e)}
                title={open ? "Close Role Manager" : "Add / Manage Roles"}
                className={`flex items-center justify-center w-7 h-7 rounded-md border border-gray-300
          text-[#223688] bg-white  transition-all
          ${open ? "bg-[#e8edff] border-[#223688]/50" : "hover:bg-[#f2f5ff]"}`}
            >
                {open ? <X className="w-4 h-4"/> : <Plus className="w-4 h-4"/>}
            </button>

            {/* === Dropdown === */}
            {open && ReactDOM.createPortal(
                <div
                    ref={dropdownRef}
                    style={{position: "absolute", top: position.top, left: position.left, zIndex: 9999}}
                    className="w-[230px] bg-white border border-gray-200
           rounded-md overflow-y-auto max-h-[calc(100vh-10rem)]
           scrollbar-thin scrollbar-thumb-gray-300"

                >
                    {loading ? (
                        <div className="flex items-center justify-center py-4 gap-2 text-gray-500 text-xs">
                            <Loader className="h-4 w-4 text-xs" message="Loading team members..."/>
                        </div>
                    ) : (
                        allRoles.map((role) => {
                            const isAssigned = userRoleIds.includes(role.id);
                            const isExpanded = expanded[role.id];
                            return (
                                <div
                                    key={role.id}
                                    className="border-b border-gray-100 last:border-none px-4 py-2.5 hover:bg-[#f9fbff]"
                                >
                                    {/* === Role Header === */}
                                    <div className="flex items-center justify-between cursor-pointer">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={isAssigned}
                                                onChange={(e) =>
                                                    handleCheckbox(role.id, e.target.checked)
                                                }
                                                className="accent-[#223688] w-4 h-4 cursor-pointer"
                                            />
                                            <div className="flex flex-col leading-tight">
                        <span className="text-md font-semibold text-gray-800">
                          {role.name}
                        </span>
                                                <span className="text-fluid-xs text-gray-500">
                          {role.description}
                        </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleExpand(role.id);
                                            }}
                                            className="text-gray-500 hover:text-[#223688]"
                                        >
                                            {isExpanded ? (
                                                <ChevronDown className="w-4 h-4"/>
                                            ) : (
                                                <ChevronRight className="w-4 h-4"/>
                                            )}
                                        </button>
                                    </div>

                                    {/* === Privileges === */}
                                    {isExpanded && role.privileges?.length > 0 && (
                                        <div className="mt-1.5 pl-6 flex flex-col gap-[0.3rem]">
                                            {role.privileges.map((priv) => (
                                                <div
                                                    key={priv.id}
                                                    className="flex items-center justify-between text-md text-gray-700 border-l border-gray-200 pl-3"
                                                >
                                                    <div className="flex flex-col leading-tight">
                            <span className="font-medium text-[#223688]">
                              {priv.menuName}
                            </span>
                                                        <span className="text-gray-500 text-[11px]">
                              {priv.description}
                            </span>
                                                    </div>
                                                    <span
                                                        className={`px-2 py-[2px] text-xs font-semibold rounded-md uppercase
                              ${
                                                            priv.action === "VIEW"
                                                                ? "text-[#223688] bg-[#e8edff]"
                                                                : priv.action === "CREATE"
                                                                    ? "text-[#1B7F48] bg-[#e7f7ed]"
                                                                    : priv.action === "APPROVE"
                                                                        ? "text-[#1565C0] bg-[#e6f0ff]"
                                                                        : priv.action === "REJECT"
                                                                            ? "text-[#C62828] bg-[#fde8e8]"
                                                                            : "text-gray-600 bg-gray-100"
                                                        }`}
                                                    >
                            {priv.action}
                          </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

export default RoleDropdown;
